import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useTonAddress } from '@tonconnect/ui-react';
import { Market } from './screens/Market.tsx';
import { Requests } from './screens/Requests.tsx';
import { RequestForm } from './screens/RequestForm.tsx';
import { Offer } from './screens/Offer.tsx';
import { MyDeals } from './screens/MyDeals.tsx';
import { Builder } from './screens/Builder.tsx';
import { Profile } from './screens/Profile.tsx';
import { Guide } from './screens/Guide.tsx';
import { Inviter } from './screens/Inviter.tsx';
import { Referral } from './screens/Referral.tsx';
import { Admin } from './screens/Admin.tsx';
import { Progress } from './components/Progress.tsx';
import { WalletButton } from './components/WalletButton.tsx';
import { getMasterAdmin, getMasterNextAdmin } from './lib/chain.ts';
import { sameAddress } from './lib/collections.ts';
import { getReferralCode, haptic, inTelegram, webApp } from './lib/telegram.ts';
import { useProofOnConnect } from './lib/confirm.ts';
import { NavMark, type NavShape } from './components/NavMark.tsx';
import { fetchWalletDeals } from './lib/api.ts';

type Screen =
  | { name: 'market' }
  | { name: 'board' }
  | { name: 'deals'; side?: 'buyer' | 'seller' }
  | { name: 'builder'; nft: string | null }
  | { name: 'profile' }
  | { name: 'offer'; address: string }
  | { name: 'request'; collection: string; item: { address: string; name: string | null } | null }
  | { name: 'guide' }
  | { name: 'referral' }
  | { name: 'inviter'; code: string }
  | { name: 'admin' };

const MASTER = import.meta.env['VITE_MASTER_ADDRESS'] ?? '';

/**
 * Вкладки нижней панели — четыре, как в макете.
 *
 * Конструктора среди них нет намеренно: в макет он попадает с доски, из
 * пустого состояния — «не ждите покупателя, выставите условия сами». Пятая
 * вкладка вытесняла бы подписи и предлагала строить оффер тому, кто ещё не
 * решил, продаёт ли он вообще.
 */
const TABS = ['market', 'board', 'deals', 'profile'] as const;

/**
 * Своя фигура у каждой вкладки.
 *
 * В макете значки отличались только скруглением, и четыре вкладки читались
 * как два кружка и два квадрата — то есть не различались. Фигура отвечает
 * за «что это», заливка и цвет — за «где я».
 */
const TAB_SHAPE: Record<(typeof TABS)[number], NavShape> = {
  market: 'square',
  board: 'triangle',
  deals: 'diamond',
  profile: 'circle',
};

export function App() {
  const { t, i18n } = useTranslation();
  const myAddress = useTonAddress(false);
  // Подпись кошелька просится прямо при подключении: отдельный обряд
  // «отключить и подключить заново» проходили не все, а без него человек
  // остаётся без писем о собственной сделке.
  useProofOnConnect(i18n.language === 'en' ? 'en' : 'ru');
  const [screen, setScreen] = useState<Screen>({ name: 'market' });

  /**
   * Приглашение ведёт на общую витрину, а не сразу к пригласившему.
   *
   * Сначала было наоборот: человек шёл по чьей-то рекомендации, и первым
   * ему показывали того, ради кого он пришёл. Но у пригласившего может не
   * быть ничего — ни офферов, ни запросов, — и тогда первым, что человек
   * видит, оказывается пустая страница. Хуже первого впечатления не
   * придумать.
   *
   * Поэтому витрина, а ссылка на пригласившего — строкой над ней. Само
   * приглашение от этого не страдает: оно живёт в хранилище и сработает
   * при первой покупке, куда бы человек ни пошёл.
   */
  const invite = getReferralCode();

  // Кто админ, знает контракт, а не настройки приложения. Проверка здесь не
  // защита — защита в том, что мастер отвергнет чужую команду, — а способ не
  // показывать кнопки, которые всё равно не сработают.
  const { data: admin } = useQuery({
    queryKey: ['masterAdmin', MASTER],
    queryFn: () => getMasterAdmin(MASTER),
    enabled: MASTER.length > 0 && myAddress.length > 0,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  const isAdmin = admin !== undefined && myAddress.length > 0 && sameAddress(admin, myAddress);

  // Преемнику панель тоже нужна, иначе принять передачу негде: второй шаг
  // делает не тот, кто её начал, а тот, кому предложили.
  const { data: heir } = useQuery({
    queryKey: ['masterNextAdmin', MASTER],
    queryFn: () => getMasterNextAdmin(MASTER),
    enabled: MASTER.length > 0 && myAddress.length > 0,
    staleTime: 60 * 1000,
    retry: false,
  });
  const isHeir =
    heir !== undefined && heir !== null && myAddress.length > 0 && sameAddress(heir, myAddress);

  /**
   * Недоделанная выкладка — оффер без залога.
   *
   * Такой оффер не виден никому: ни покупателям, ни самому продавцу, если
   * он не заглянет в «Сделки» на вкладку продавца. Человек уходит в
   * уверенности, что выложил гифт, и возвращается к пустой витрине.
   * Поэтому напоминание висит поверх любого экрана, пока шаг не сделан.
   *
   * Ключ запроса тот же, что у «Сделок», — это их общий кэш, а не второй
   * поход на сервер.
   */
  const { data: walletDeals } = useQuery({
    queryKey: ['myDeals', myAddress],
    queryFn: () => fetchWalletDeals(myAddress),
    enabled: myAddress.length > 0,
  });
  const unfinished = (walletDeals?.deals ?? []).filter(
    (d) => d.state === 0 && sameAddress(d.seller, myAddress),
  );

  const goBack = useCallback(
    () =>
      setScreen((s) => {
        if (s.name === 'guide' || s.name === 'admin' || s.name === 'referral') {
          return { name: 'profile' };
        }
        // Конструктор открывают с доски — туда же и возвращаемся.
        if (s.name === 'builder') return { name: 'board' };
        if (s.name === 'inviter') return { name: 'market' };
        return { name: 'market' };
      }),
    [],
  );

  const nested = screen.name === 'offer' || screen.name === 'request' ||
    screen.name === 'guide' || screen.name === 'admin' ||
    screen.name === 'referral' || screen.name === 'builder';

  // Кнопка «назад» — родная телеграмовская: в Mini App её ищут именно там.
  // Своя в шапке тоже есть (макет), но вне Telegram она единственная.
  useEffect(() => {
    const back = webApp?.BackButton;
    if (back === undefined) return;
    if (!nested) {
      back.hide();
      return;
    }
    back.show();
    back.onClick(goBack);
    return () => back.offClick(goBack);
  }, [nested, goBack]);

  const titles: Record<Screen['name'], [string, string]> = {
    market: [t('shell.nav_market'), t('shell.sub_market')],
    board: [t('shell.nav_board'), t('shell.sub_board')],
    deals: [t('shell.nav_deals'), t('shell.sub_deals')],
    builder: [t('shell.nav_builder'), t('shell.sub_builder')],
    profile: [t('shell.nav_profile'), t('shell.sub_profile')],
    offer: [t('nav.offer'), t('shell.sub_offer')],
    request: [t('request.new'), t('shell.sub_request')],
    guide: [t('guide.how_works'), t('shell.sub_guide')],
    referral: [t('ref.title'), t('shell.sub_referral')],
    inviter: [t('inviter.title'), t('shell.sub_inviter')],
    admin: [t('admin.title'), t('shell.sub_admin')],
  };
  const [title, sub] = titles[screen.name];

  const tabLabel: Record<(typeof TABS)[number], string> = {
    market: t('shell.nav_market'),
    board: t('shell.nav_board'),
    deals: t('shell.nav_deals'),
    profile: t('shell.nav_profile'),
  };

  return (
    <div className="app">
      <Progress />

      {/* Шапка макета: возврат, заголовок с подзаголовком, метка сети. */}
      <header className="appbar">
        {nested ? (
          <button className="iconbtn" onClick={() => { haptic(); goBack(); }} aria-label="назад">‹</button>
        ) : (
          <>
            <img className="logo logo-dark" src="/logo-mark.svg" alt="78" height={20} />
            <img className="logo logo-light" src="/logo-mark-light.svg" alt="78" height={20} />
          </>
        )}
        <div className="col" style={{ flex: 1, gap: 1, minWidth: 0 }}>
          <div className="appbar-title">{title}</div>
          <div className="appbar-sub">{sub}</div>
        </div>
        {/* Кошелёк в шапке и всегда на виду: это единственное, что человек
            проверяет перед каждым действием с деньгами. */}
        <WalletButton />
      </header>

      {!inTelegram && (
        <div className="card card-flat text" style={{ margin: '14px 14px 0' }}>
          {t('profile.telegram_only')}
        </div>
      )}

      {/* Напоминание о незаконченной выкладке. На самих «Сделках» его нет:
          там этот оффер и так первый на экране, своей карточкой. */}
      {unfinished.length > 0 && screen.name !== 'deals' && (
        <div className="card card-lime row" style={{ margin: '14px 14px 0', gap: 10 }}>
          <span className="text" style={{ flex: 1 }}>{t('shell.unfinished')}</span>
          <button
            className="btn btn-sm"
            style={{ width: 'auto', flexShrink: 0 }}
            onClick={() => { haptic(); setScreen({ name: 'deals', side: 'seller' }); }}
          >
            {t('shell.finish')}
          </button>
        </div>
      )}

      {/* key по имени экрана: без него React переиспользует узлы, и переход
          происходит мгновенной подменой текста без всякого движения. */}
      <div key={screen.name} style={{ display: 'contents' }}>
        {screen.name === 'market' && (
          <Market
            invite={invite === '' ? null : invite}
            onInviter={() => { haptic(); setScreen({ name: 'inviter', code: invite }); }}
            onOpen={(address) => { haptic(); setScreen({ name: 'offer', address }); }}
            onRequest={(collection, item) => { haptic(); setScreen({ name: 'request', collection, item: item ?? null }); }}
          />
        )}
        {screen.name === 'offer' && (
          <Offer address={screen.address} onGuide={() => { haptic(); setScreen({ name: 'guide' }); }} />
        )}
        {screen.name === 'board' && (
          <Requests onBuilder={(nft) => { haptic(); setScreen({ name: 'builder', nft: nft ?? null }); }} />
        )}
        {screen.name === 'request' && (
          <RequestForm collection={screen.collection} item={screen.item} onDone={goBack} />
        )}
        {screen.name === 'deals' && <MyDeals initialSide={screen.side} />}
        {screen.name === 'builder' && (
          <Builder
            masterAddress={MASTER}
            preselect={screen.nft}
            onPublished={() => { haptic(); setScreen({ name: 'deals', side: 'seller' }); }}
          />
        )}
        {screen.name === 'profile' && (
          <Profile
            onGuide={() => { haptic(); setScreen({ name: 'guide' }); }}
            onReferral={() => { haptic(); setScreen({ name: 'referral' }); }}
            onAdmin={
              isAdmin || isHeir ? () => { haptic(); setScreen({ name: 'admin' }); } : undefined
            }
          />
        )}
        {screen.name === 'inviter' && (
          <Inviter
            code={screen.code}
            onOpen={(address) => { haptic(); setScreen({ name: 'offer', address }); }}
            onMarket={() => { haptic(); setScreen({ name: 'market' }); }}
            onBoard={() => { haptic(); setScreen({ name: 'board' }); }}
          />
        )}
        {screen.name === 'guide' && <Guide />}
        {screen.name === 'referral' && (
          <Referral onProfile={() => { haptic(); setScreen({ name: 'profile' }); }} />
        )}
        {screen.name === 'admin' && (
          <Admin
            masterAddress={MASTER}
            isAdmin={isAdmin}
            isHeir={isHeir}
            admin={admin ?? null}
          />
        )}
      </div>

      <nav className="tabbar">
        {TABS.map((name) => {
          const selected =
            screen.name === name ||
            (name === 'profile' &&
              (screen.name === 'guide' ||
                screen.name === 'admin' ||
                screen.name === 'referral')) ||
            (name === 'board' && screen.name === 'builder') ||
            (name === 'market' &&
              (screen.name === 'offer' ||
                screen.name === 'request' ||
                screen.name === 'inviter'));
          return (
            <button
              key={name}
              aria-selected={selected}
              onClick={() => { haptic(); setScreen({ name } as Screen); }}
            >
              <NavMark shape={TAB_SHAPE[name]} active={selected} />
              {tabLabel[name]}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
